/**
 * TextsGuide: the visual guide of the Texts tab (/questions/texts, /my-questions/texts), opened by the "?" after the
 * page's title (HelpTitle helpId="texts"). A passage is one text, table or chart that several context questions share.
 * - It follows the screen it explains, at the page's own breakpoints: FolderBar's location button and "⋯", and the
 *   passage cards instead of the table, under 768px; the "Filters" toggle under 640px.
 * - It follows the reader's role: creators (the page's rule, `canCreate`, or the account's roles while the page's
 *   folder lists load; experts too since they write questions, 2026-10-28) get the create, write and link steps; a
 *   role that would edit passages without creating them (none today) the open and link steps; readers the read and
 *   tag steps. The create step draws the reader's own role, with Share
 *   where the role shares the subject; the lock step the printed passage's dialog as the role gets it (its name still
 *   editable where the role edits every passage, the booklet's link for an admin).
 * Props, from the page: `canCreate` (who makes passages there), `mine` (the Texts tab of My Questions, the user's own
 * passages only), `buildsTests` (the user opens the test bank, where "Context question" adds a passage to a test).
 */
import React, { useContext } from "react";
import { useTranslation } from "react-i18next";
import {
  AdjustmentsHorizontalIcon,
  ArrowDownIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpIcon,
  BookOpenIcon,
  CheckBadgeIcon,
  ChevronUpDownIcon,
  DocumentArrowUpIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  FolderOpenIcon,
  KeyIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  PhotoIcon,
  PlusIcon,
  QueueListIcon,
  ShareIcon,
  ShieldCheckIcon,
  Squares2X2Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { HelpFlow, HelpIntro, HelpRoles, HelpStep, HelpSteps, HelpText, HelpTips } from "../HelpKit";
import {
  MiniBadge,
  MiniButton,
  MiniCard,
  MiniCheck,
  MiniChip,
  MiniDialog,
  MiniDivider,
  MiniField,
  MiniIcon,
  MiniMenu,
  MiniRow,
  MiniScreen,
  MiniStack,
  MiniStatus,
  MiniTable,
  MiniTabs,
  MiniText,
} from "../Mini";
import { useMediaQuery } from "../../../pages/Exams/TestBuilder/ui";
import { UserContext } from "../../../utils/contexts/UserContext";
import { canCreateIn, canEditAllIn, canShareFolder, isAdminUser } from "../../../utils/folders";

const ROLE_ICONS = { admin: KeyIcon, moderator: ShieldCheckIcon, expert: CheckBadgeIcon, author: PencilIcon, viewer: EyeIcon };

export default function TextsGuide({ canCreate, mine = false, buildsTests = false }) {
  const [t] = useTranslation();
  const { user } = useContext(UserContext) || {};
  // The page's own breakpoints: FolderBar and the list (table / cards) at md, FilterPanel at sm.
  const wide = useMediaQuery("(min-width: 768px)");
  const roomy = useMediaQuery("(min-width: 640px)");
  const ex = (key) => t(`help.common.example.${key}`);

  const isAdmin = isAdminUser(user);
  const perms = user?.perms || [];
  const folderRoles = user?.folder_roles || [];
  const globalWrite = perms.includes("create_question") || perms.includes("create_exam");
  // PassageDialog's rules: who makes, edits, renames (a printed passage) and deletes passages, and who writes questions
  // for one. The page's `canCreate` stays false until its folder lists load: the account's roles say it meanwhile.
  const creates = !!canCreate || isAdmin || globalWrite || folderRoles.some(canCreateIn);
  const edits = creates || isAdmin || globalWrite || folderRoles.some(canEditAllIn);
  const renames = isAdmin || globalWrite || folderRoles.some(canEditAllIn);
  const deletes = isAdmin || globalWrite || folderRoles.some((role) => role === "moderator" || role === "author");
  const writesQuestions = isAdmin || perms.includes("create_question") || folderRoles.some(canCreateIn);
  // The create step draws the reader's own role in the subject (admins: admin), one that creates.
  const role = isAdmin ? "admin" : folderRoles.find(canCreateIn) || "author";
  const RoleIcon = ROLE_ICONS[role] || PencilIcon;
  const roleTitle = t("folders.role_title", { role: t(`folders.roles.${role}`, { defaultValue: role }) });
  const shares = canShareFolder(role);
  // Subject chips carry the reader's role icon, but for admins (FolderBar's SubjectChip).
  const subjectIcon = isAdmin ? undefined : ROLE_ICONS[folderRoles[0]];

  const texts = t("passages.tab_texts");
  const questions = t("passages.tab_questions");
  const create = t("passages.new");
  const save = t("buttons.save");
  const tabs = [
    { label: questions, icon: QueueListIcon },
    { label: texts, icon: BookOpenIcon },
  ];
  const screenTitle = mine ? t("nav.my_questions") : t("nav.question_bank");
  const subjectPath = `${ex("exam")} · ${ex("subject")}`;
  const passageName = (name) => (
    <MiniRow className="flex-nowrap">
      <MiniIcon icon={BookOpenIcon} tone="blue" />
      <MiniText variant="strong" truncate>
        {name}
      </MiniText>
    </MiniRow>
  );
  // A passage of the list on phones (a card: name, place, questions).
  const passageCard = (name, count, mark = false) => (
    <MiniCard key={name} mark={mark}>
      {passageName(name)}
      <MiniText variant="muted" truncate>
        {subjectPath}
      </MiniText>
      <MiniRow>
        <MiniBadge>{t("question_list.count", { count })}</MiniBadge>
      </MiniRow>
    </MiniCard>
  );
  // A question of the passage, numbered, with its status (the passage's own view).
  const memberRow = (number, name) => (
    <MiniCard key={number}>
      <MiniRow className="flex-nowrap justify-between">
        <MiniText truncate>{`${number}. ${name}`}</MiniText>
        <MiniStatus code="APPROVED" />
      </MiniRow>
    </MiniCard>
  );
  // A picked question with its order buttons (↑ ↓ ✕).
  const pickedRow = (number, name, markUp = false) => (
    <MiniCard
      key={number}
      aside={
        <MiniRow className="flex-nowrap">
          <MiniButton variant="quiet" icon={ArrowUpIcon} mark={markUp ? "2" : undefined} />
          <MiniButton variant="quiet" icon={ArrowDownIcon} />
          <MiniButton variant="quiet" icon={XMarkIcon} />
        </MiniRow>
      }
    >
      <MiniText truncate>{`${number}. ${name}`}</MiniText>
    </MiniCard>
  );

  const stepFind = (
    <HelpStep
      key="find"
      title={t("help.texts.steps.find.title")}
      illustration={
        wide ? (
          <MiniScreen title={screenTitle}>
            <MiniTabs items={tabs} current={1} />
            <MiniRow className="gap-y-3">
              <MiniText variant="caps">{t("folders.label")}</MiniText>
              <MiniChip variant="folder">{t("passages.all_texts")}</MiniChip>
              <MiniChip variant="folder" selected mark="1">
                {ex("exam")}
              </MiniChip>
              <MiniChip variant="folder">{ex("exam_2")}</MiniChip>
            </MiniRow>
            <MiniDivider />
            <MiniRow className="gap-y-3">
              <MiniText variant="muted">{t("folders.subjects_label")}:</MiniText>
              <MiniChip variant="subject">{t("folders.all_subjects")}</MiniChip>
              <MiniChip variant="subject" selected icon={subjectIcon} mark="2">
                {ex("subject")}
              </MiniChip>
              <MiniChip variant="subject" icon={subjectIcon}>
                {ex("subject_2")}
              </MiniChip>
            </MiniRow>
          </MiniScreen>
        ) : (
          <MiniScreen title={screenTitle}>
            <MiniTabs items={tabs} current={1} />
            <MiniCard tone="blue" icon={FolderOpenIcon} aside={<MiniIcon icon={ChevronUpDownIcon} />} mark="1">
              <MiniStack tight>
                <MiniText variant="muted" truncate>
                  {ex("exam")}
                </MiniText>
                <MiniText variant="strong" truncate>
                  {ex("subject")}
                </MiniText>
              </MiniStack>
            </MiniCard>
            <MiniMenu
              full
              title={t("folders.choose_location")}
              items={[
                { label: t("passages.all_texts"), icon: Squares2X2Icon },
                { label: ex("exam"), icon: FolderOpenIcon },
                { label: ex("subject"), indent: true, selected: true, mark: "2" },
                { label: ex("subject_2"), indent: true },
              ]}
            />
          </MiniScreen>
        )
      }
    >
      <HelpText i18nKey={wide ? "help.texts.steps.find.text" : "help.texts.steps.find.text_phone"} values={{ texts, questions }} />
      {mine && (
        <p>
          <HelpText i18nKey="help.texts.steps.find.text_mine" values={{ mine: t("nav.my_questions") }} />
        </p>
      )}
    </HelpStep>
  );

  const stepSearch = (
    <HelpStep
      key="search"
      title={t("help.texts.steps.search.title")}
      illustration={
        <MiniCard>
          {roomy ? (
            <MiniRow>
              <MiniField icon={MagnifyingGlassIcon} placeholder={t("passages.search")} className="flex-1" mark />
              <MiniField select value={t("questions_page.filters.all_subjects")} />
            </MiniRow>
          ) : (
            <MiniRow className="flex-nowrap">
              <MiniField icon={MagnifyingGlassIcon} placeholder={t("passages.search")} className="flex-1" mark />
              <MiniButton variant="outline" icon={AdjustmentsHorizontalIcon} chevron>
                {t("question_list.filters_short")}
              </MiniButton>
            </MiniRow>
          )}
        </MiniCard>
      }
    >
      {roomy ? (
        t("help.texts.steps.search.text")
      ) : (
        <HelpText i18nKey="help.texts.steps.search.text_phone" values={{ filters: t("question_list.filters_short") }} />
      )}
    </HelpStep>
  );

  const stepCreate = (
    <HelpStep
      key="create"
      title={t("help.texts.steps.create.title")}
      illustration={
        <MiniScreen title={subjectPath}>
          {wide ? (
            <MiniRow className="justify-between gap-y-3">
              <MiniRow>
                <MiniText variant="title">{ex("subject")}</MiniText>
                <MiniBadge icon={RoleIcon}>{roleTitle}</MiniBadge>
              </MiniRow>
              <MiniRow>
                {shares && (
                  <MiniButton variant="quiet" icon={ShareIcon}>
                    {t("folders.share.button")}
                  </MiniButton>
                )}
                <MiniButton variant="primary" icon={PlusIcon} mark>
                  {create}
                </MiniButton>
              </MiniRow>
            </MiniRow>
          ) : (
            <>
              <MiniRow>
                <MiniBadge icon={RoleIcon}>{roleTitle}</MiniBadge>
              </MiniRow>
              <MiniRow className="flex-nowrap">
                <MiniButton variant="primary" icon={PlusIcon} mark>
                  {create}
                </MiniButton>
                {shares && <MiniButton variant="outline" icon={EllipsisHorizontalIcon} />}
              </MiniRow>
            </>
          )}
        </MiniScreen>
      }
    >
      <HelpText i18nKey="help.texts.steps.create.text" values={{ create, folder: t("folders.subject_folder") }} />
    </HelpStep>
  );

  const stepWrite = (
    <HelpStep
      key="write"
      title={t("help.texts.steps.write.title")}
      illustration={
        <MiniDialog
          title={t("passages.dialog.title_new")}
          footer={[
            <MiniButton key="close" variant="quiet">
              {t("buttons.close")}
            </MiniButton>,
            <MiniButton key="save" variant="primary">
              {save}
            </MiniButton>,
          ]}
        >
          <MiniField label={t("passages.editor.name")} value={ex("passage")} />
          <MiniField label={t("folders.subject_folder")} value={ex("subject")} select />
          <MiniRow className="justify-between gap-y-3">
            <MiniText variant="strong">{t("passages.editor.body")}</MiniText>
            <MiniRow>
              <MiniButton variant="quiet" icon={PhotoIcon}>
                {t("passages.editor.insert_image")}
              </MiniButton>
              <MiniButton variant="quiet" icon={DocumentArrowUpIcon} mark="2">
                {t("passages.editor.upload_docx")}
              </MiniButton>
            </MiniRow>
          </MiniRow>
          <MiniCard mark="1">
            <MiniText>{t("help.texts.example.body")}</MiniText>
          </MiniCard>
        </MiniDialog>
      }
    >
      <HelpText
        i18nKey="help.texts.steps.write.text"
        values={{ image: t("passages.editor.insert_image"), docx: t("passages.editor.upload_docx") }}
      />
    </HelpStep>
  );

  const stepLink = (
    <HelpStep
      key="link"
      title={t("help.texts.steps.link.title")}
      illustration={
        <MiniStack>
          <MiniRow className="justify-between">
            <MiniText variant="strong">{t("passages.dialog.questions")}</MiniText>
            {writesQuestions && (
              <MiniButton variant="link" icon={PlusIcon}>
                {t("passages.dialog.create_question")}
              </MiniButton>
            )}
          </MiniRow>
          {/* The row's text ticks it too (QuestionPicker): the whole row is marked. */}
          <MiniCard icon={<MiniCheck checked />} mark="1">
            <MiniText variant="strong" truncate>
              {ex("question_1")}
            </MiniText>
            <MiniRow>
              <MiniStatus code="APPROVED" />
            </MiniRow>
          </MiniCard>
          <MiniCard icon={<MiniCheck />}>
            <MiniText variant="muted" truncate>
              {ex("question_3")}
            </MiniText>
            <MiniRow>
              <MiniStatus code="DRAFT" />
            </MiniRow>
          </MiniCard>
          <MiniText variant="strong">{t("passages.picker.selected", { count: 2 })}</MiniText>
          {pickedRow(1, ex("question_2"))}
          {pickedRow(2, ex("question_1"), true)}
        </MiniStack>
      }
    >
      <HelpText i18nKey="help.texts.steps.link.text" values={{ section: t("passages.dialog.questions"), save }} />
      {writesQuestions && (
        <p>
          <HelpText i18nKey="help.texts.steps.link.text_create" values={{ create: t("passages.dialog.create_question") }} />
        </p>
      )}
    </HelpStep>
  );

  const stepOpen = (
    <HelpStep
      key="open"
      title={edits ? t("help.texts.steps.open.title") : t("help.texts.steps.open.title_read")}
      illustration={
        wide ? (
          <MiniTable
            columns={[
              { label: t("passages.columns.name"), width: "48%" },
              t("question_list.columns.subject"),
              { label: t("passages.columns.questions"), width: "7rem" },
            ]}
            rows={[
              [passageName(ex("passage")), ex("subject"), <MiniBadge>{t("question_list.count", { count: 3 })}</MiniBadge>],
              [passageName(t("help.texts.example.passage_2")), ex("subject_2"), <MiniBadge>{t("question_list.count", { count: 2 })}</MiniBadge>],
            ]}
            markRow={0}
          />
        ) : (
          <MiniStack>
            {passageCard(ex("passage"), 3, true)}
            {passageCard(t("help.texts.example.passage_2"), 2)}
          </MiniStack>
        )
      }
    >
      <HelpText i18nKey={wide ? "help.texts.steps.open.click" : "help.texts.steps.open.tap"} />{" "}
      {edits ? <HelpText i18nKey="help.texts.steps.open.edit" values={{ save }} /> : t("help.texts.steps.open.read")}
    </HelpStep>
  );

  // The printed passage's dialog as the reader's role gets it (PassageDialog, PassageLock): an admin reads the booklet
  // as a link, not by its name; a role that edits every passage of the subject still renames it (Close and Save),
  // anyone else only reads it (a framed Close).
  const booklet = t("help.texts.example.booklet");
  const lockText = isAdmin
    ? `${t("passages.errors.question_in_booklet_linked", { count: 1 })} ${t("question_detail.booklet_lock.return", { count: 1 })}`
    : t("passages.errors.question_in_booklet", { names: booklet });
  const stepLock = (
    <HelpStep
      key="lock"
      title={t("help.texts.steps.lock.title")}
      illustration={
        <MiniDialog
          title={t("passages.dialog.title")}
          footer={
            renames
              ? [
                  <MiniButton key="close" variant="quiet">
                    {t("buttons.close")}
                  </MiniButton>,
                  <MiniButton key="save" variant="primary">
                    {save}
                  </MiniButton>,
                ]
              : [
                  <MiniButton key="close" variant="outline">
                    {t("buttons.close")}
                  </MiniButton>,
                ]
          }
        >
          <MiniCard tone="blue" icon={LockClosedIcon} mark={{ pointer: false }}>
            <MiniText>{lockText}</MiniText>
            {isAdmin && (
              <MiniRow className="flex-nowrap">
                <MiniText variant="strong" truncate>
                  {booklet}
                </MiniText>
                <MiniIcon icon={ArrowTopRightOnSquareIcon} tone="blue" />
              </MiniRow>
            )}
            {/* A row of its own: two texts in a card would run on in one line. */}
            {renames && (
              <MiniRow>
                <MiniText>{t("passages.lock.name_only")}</MiniText>
              </MiniRow>
            )}
          </MiniCard>
          {renames && (
            <MiniRow>
              <MiniField label={t("passages.editor.name")} value={ex("passage")} className="flex-1" />
            </MiniRow>
          )}
          {memberRow(1, ex("question_1"))}
          {memberRow(2, ex("question_2"))}
        </MiniDialog>
      }
    >
      {/* Paragraphs all: the step's text spaces them evenly. */}
      <p>{t("help.texts.steps.lock.text")}</p>
      {renames && <p>{t("passages.lock.name_only")}</p>}
      {isAdmin && <p>{t("help.texts.steps.lock.text_admin")}</p>}
    </HelpStep>
  );

  const stepTag = (
    <HelpStep
      key="tag"
      title={t("help.texts.steps.tag.title")}
      illustration={
        <MiniScreen title={screenTitle}>
          <MiniTabs items={tabs} current={0} />
          <MiniCard>
            <MiniText variant="strong">{ex("question_2")}</MiniText>
            <MiniRow className="gap-y-3">
              <MiniStatus code="APPROVED" />
              <MiniBadge icon={BookOpenIcon} tone="indigo" mark>
                {ex("passage")}
              </MiniBadge>
            </MiniRow>
          </MiniCard>
        </MiniScreen>
      }
    >
      <HelpText i18nKey="help.texts.steps.tag.text" values={{ questions }} />
    </HelpStep>
  );

  return (
    <>
      <HelpIntro>{t("help.texts.intro")}</HelpIntro>

      <HelpFlow pipeline="bts" current="questions" />

      {/* Creators (experts among them): find, search, create, write, link, open, lock. Editors who don't create (no
          role today): find, search, open, link, lock. Readers: find, search, open (read), tag. */}
      <HelpSteps>
        {stepFind}
        {stepSearch}
        {creates && stepCreate}
        {creates && stepWrite}
        {creates && stepLink}
        {stepOpen}
        {edits && !creates && stepLink}
        {edits && stepLock}
        {!edits && stepTag}
      </HelpSteps>

      <HelpRoles
        rows={[
          { key: "admin", role: t("folders.roles.admin"), Icon: KeyIcon, can: t("help.texts.roles.admin") },
          { key: "moderator", role: t("folders.roles.moderator"), Icon: ShieldCheckIcon, can: t("help.texts.roles.moderator") },
          { key: "expert", role: t("folders.roles.expert"), Icon: CheckBadgeIcon, can: t("help.texts.roles.expert") },
          { key: "author", role: t("folders.roles.author"), Icon: PencilIcon, can: t("help.texts.roles.author") },
          { key: "viewer", role: t("folders.roles.viewer"), Icon: EyeIcon, can: t("help.texts.roles.viewer") },
        ]}
      />

      <HelpTips
        items={[
          // Readers have this as a step.
          edits && <HelpText key="tag" i18nKey="help.texts.steps.tag.text" values={{ questions }} />,
          // An editor who doesn't create orders a passage's questions (creators have it in the link step); readers only
          // see the order.
          edits ? !creates && t("help.texts.tips.order") : t("help.texts.tips.order_read"),
          deletes && <HelpText key="delete" i18nKey="help.texts.tips.delete" values={{ delete: t("passages.dialog.delete") }} />,
          creates && (
            <HelpText
              key="images"
              i18nKey="help.texts.tips.word_images"
              values={{ image: t("passages.editor.insert_image"), docx: t("passages.editor.upload_docx") }}
            />
          ),
          buildsTests && <HelpText key="context" i18nKey="help.texts.tips.context" values={{ context: t("passages.context.button") }} />,
          !edits && t("passages.editor.name_hint"),
        ]}
      />
    </>
  );
}
